import { useEffect, useRef } from "react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useLogStream } from "@/hooks/use-log-stream";
import { useLogSettings } from "@/hooks/use-log-settings";
import { Pause, Play, Trash2, Wifi, WifiOff, ScrollText } from "lucide-react";

export default function LogsPage() {
  const { lines, isConnected, isPaused, togglePause, clearLogs } = useLogStream();
  const {
    debugLevel,
    verboseLevel,
    pjsipLoggerEnabled,
    setDebugLevel,
    setVerboseLevel,
    setPjsipLogger,
    isLoading,
  } = useLogSettings();

  const terminalRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  useEffect(() => {
    if (autoScrollRef.current && terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [lines]);

  const handleScroll = () => {
    if (!terminalRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = terminalRef.current;
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 50;
  };

  return (
    <div className="flex h-full -m-6">
      <aside className="flex flex-col w-60 bg-sidebar border-r border-border p-4 gap-4">
        <div className="flex items-center gap-2">
          <ScrollText size={18} className="text-muted-foreground" />
          <span className="font-semibold text-sm">Log Settings</span>
        </div>

        <Separator />

        <div className="space-y-3" role="group" aria-label="Logging controls">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">
                Debug Level
              </label>
              <span className="text-xs font-mono text-foreground tabular-nums">
                {debugLevel}
              </span>
            </div>
            <Slider
              value={debugLevel}
              min={0}
              max={10}
              onChange={setDebugLevel}
              disabled={isLoading}
            />
            <p className="text-[11px] text-muted-foreground/70">
              core set debug {debugLevel}
            </p>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">
                Verbose Level
              </label>
              <span className="text-xs font-mono text-foreground tabular-nums">
                {verboseLevel}
              </span>
            </div>
            <Slider
              value={verboseLevel}
              min={0}
              max={10}
              onChange={setVerboseLevel}
              disabled={isLoading}
            />
            <p className="text-[11px] text-muted-foreground/70">
              core set verbose {verboseLevel}
            </p>
          </div>
        </div>

        <Separator />

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">
            PJSIP Logger
          </label>
          <Switch
            checked={pjsipLoggerEnabled}
            onChange={(e) => setPjsipLogger(e.target.checked)}
            disabled={isLoading}
            label={pjsipLoggerEnabled ? "Enabled" : "Disabled"}
          />
        </div>

        <Separator />

        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            {isConnected ? (
              <Wifi size={14} className="text-green-500" />
            ) : (
              <WifiOff size={14} className="text-red-400" />
            )}
            <span className="text-xs text-muted-foreground">
              {isConnected ? "Connected" : "Reconnecting..."}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-8 text-xs"
              onClick={togglePause}
            >
              {isPaused ? (
                <Play size={13} />
              ) : (
                <Pause size={13} />
              )}
              {isPaused ? "Resume" : "Pause"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={clearLogs}
              title="Clear logs"
            >
              <Trash2 size={13} />
            </Button>
          </div>
        </div>
      </aside>

      <div
        ref={terminalRef}
        onScroll={handleScroll}
        className="flex-1 overflow-auto bg-black font-mono text-xs leading-5 p-4"
      >
        {lines.length === 0 && (
          <div className="text-gray-600 flex items-center justify-center h-full select-none">
            {isConnected ? "Waiting for logs..." : "Connecting..."}
          </div>
        )}
        {lines.map((line, i) => (
          <div
            key={i}
            className={cn(
              "whitespace-pre-wrap break-all",
              line.includes("WARNING") && "text-yellow-400",
              line.includes("ERROR") && "text-red-400",
              line.includes("NOTICE") && "text-green-400",
              line.includes("DEBUG") && "text-blue-400",
              line.includes("VERBOSE") && "text-cyan-400",
              !line.includes("WARNING") &&
                !line.includes("ERROR") &&
                !line.includes("NOTICE") &&
                !line.includes("DEBUG") &&
                !line.includes("VERBOSE") &&
                "text-gray-300"
            )}
          >
            {line || "\u00A0"}
          </div>
        ))}
      </div>
    </div>
  );
}